import { Vector3 } from '@babylonjs/core/Maths';
import { MirrorFeature } from '@models/classes/features/mirror-feature';
import { SubmeshData } from '@models/classes/features/submesh-feature';
import { Token } from '@models/classes/token';
import { SubmeshVertexData } from '@view/helpers/geometry-helper';
import { SliceFeatureRenderer } from './slice';

export class MirrorFeatureRenderer extends SliceFeatureRenderer {
    modelToken = Token.MirrorFeature;
    updateMeshGeometry(
        feature: MirrorFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges);

        if (
            newVertexData.positions &&
            newVertexData.positions.length > 0 &&
            newVertexData.indices &&
            newVertexData.indices.length > 0
        ) {
            const positions = newVertexData.positions;
            const normals = newVertexData.normals;
            const uvs = newVertexData.uvs;
            const indices = newVertexData.indices;

            const newIndices = new Int32Array(indices.length);
            const newPositions = new Float32Array(positions.length);
            const newNormals = new Float32Array(normals?.length || 0);

            const workingVector = Vector3.Zero();
            const planeNormal = feature.normal.toVec3().normalize();
            const planeIntersect = feature.intersect.toVec3();
            const vertexCount = positions.length / 3;
            for (let i = 0; i < vertexCount; i++) {
                const idx = i * 3;
                workingVector.set(positions[idx], positions[idx + 1], positions[idx + 2]);
                workingVector.subtractInPlace(planeIntersect);
                let d = Vector3.Dot(workingVector, planeNormal);
                planeNormal.scaleAndAddToRef(-2 * d, workingVector);
                newPositions[idx] = workingVector.x + planeIntersect.x;
                newPositions[idx + 1] = workingVector.y + planeIntersect.y;
                newPositions[idx + 2] = workingVector.z + planeIntersect.z;

                if (normals) {
                    workingVector.set(normals[idx], normals[idx + 1], normals[idx + 2]);
                    d = Vector3.Dot(workingVector, planeNormal);
                    planeNormal.scaleAndAddToRef(-2 * d, workingVector);
                    newNormals[idx] = workingVector.x;
                    newNormals[idx + 1] = workingVector.y;
                    newNormals[idx + 2] = workingVector.z;
                }
            }

            // Flip the direction of the triangles so that the normals face the right way
            const triCount = indices.length / 3;
            for (let i = 0; i < triCount; i++) {
                const idx = i * 3;
                if (feature.cloneGeometry) {
                    newIndices[idx] = vertexCount + indices[idx + 2];
                    newIndices[idx + 1] = vertexCount + indices[idx + 1];
                    newIndices[idx + 2] = vertexCount + indices[idx];
                } else {
                    newIndices[idx] = indices[idx + 2];
                    newIndices[idx + 1] = indices[idx + 1];
                    newIndices[idx + 2] = indices[idx];
                }
            }

            if (feature.cloneGeometry) {
                const combinedIndices = new Int32Array(indices.length * 2);
                const combinedPositions = new Float32Array(positions.length * 2);
                const combinedNormals = new Float32Array((normals?.length || 0) * 2);
                const combinedUvs = new Float32Array((uvs?.length || 0) * 2);
                const combinedSubmeshes: SubmeshData[] = [];

                combinedIndices.set(indices);
                combinedIndices.set(newIndices, indices.length);
                combinedPositions.set(positions);
                combinedPositions.set(newPositions, positions.length);
                if (normals) {
                    combinedNormals.set(normals);
                    combinedNormals.set(newNormals, normals.length);
                }
                if (uvs) {
                    combinedUvs.set(uvs);
                    combinedUvs.set(uvs, uvs.length);
                }

                if (newVertexData.submeshData) {
                    newVertexData.submeshData.forEach(submesh => {
                        const newSubmesh = {
                            ...submesh,
                            verticesStart: submesh.verticesStart + vertexCount,
                            indexStart: submesh.indexStart + indices.length,
                        };
                        combinedSubmeshes.push(submesh, newSubmesh);
                    });
                }

                newVertexData.indices = combinedIndices;
                newVertexData.positions = combinedPositions;
                if (normals) {
                    newVertexData.normals = combinedNormals;
                }
                if (uvs) {
                    newVertexData.uvs = combinedUvs;
                }
                newVertexData.submeshData = combinedSubmeshes;
            } else {
                newVertexData.indices = newIndices;
                newVertexData.positions = newPositions;
                if (normals) {
                    newVertexData.normals = newNormals;
                }
            }
        }

        return newVertexData;
    }
}
